import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Text, NumberInput, DateInput, TimeInput, Dropdown, MultiSelect, Boolean, Subheader, Instructions, Checkbox, NeutralToggle, SearchSelect } from './FieldComponents';
import { fetchUsers } from './CreateLead';

// Fallback list in case the fields endpoint fails
const defaultTypeOptions = [
    { id: 1, label: 'Text - Single Line' },
    { id: 2, label: 'Text - Multi Line' },
    { id: 3, label: 'Amount - Currency' },
    { id: 4, label: 'Amount - Percent' },
    { id: 5, label: 'Amount - Number' },
    { id: 6, label: 'Date' },
    { id: 7, label: 'Time' },
    { id: 8, label: 'Person' },
    { id: 9, label: 'Person List' },
    { id: 10, label: 'Dropdown List' },
    { id: 11, label: 'Multi-Select List' },
    { id: 12, label: 'Yes/No' },
    { id: 15, label: 'File' },
    { id: 16, label: 'Multi File' },
    { id: 17, label: 'Calculation' },
    { id: 18, label: 'DocGen' },
    { id: 20, label: 'Deadline' },
];

const toCst = (dt) => {
    if (!dt) return '';
    // Normalize to UTC by appending Z when no offset is present
    const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dt.trim()) ? dt : `${dt}Z`;
    const parsed = new Date(normalized);
    if (isNaN(parsed.getTime())) return dt;
    return parsed.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        dateStyle: 'short',
        timeStyle: 'short'
    });
};

const parseDeadlineVitalValue = (rawValue) => {
    if (!rawValue) return { due: '', done: '' };

    if (typeof rawValue === 'object') {
        return {
            due: rawValue.due || '',
            done: rawValue.done || ''
        };
    }

    const rawString = String(rawValue).trim();
    if (!rawString) return { due: '', done: '' };

    try {
        const parsed = JSON.parse(rawString);
        const parsedValue = Array.isArray(parsed) ? parsed[0] : parsed;
        if (parsedValue && typeof parsedValue === 'object') {
            return {
                due: parsedValue.due || parsedValue.date || parsedValue.value || '',
                done: parsedValue.done || ''
            };
        }
    } catch {
    }

    return { due: rawString, done: '' };
};

const serializeDeadlineVitalValue = (deadlineValue) => JSON.stringify({
    due: deadlineValue?.due || '',
    done: deadlineValue?.done || ''
});

const Vitals = ({ caseId, leadId, caseData, caseTypes, fetchCase, fetchVitals, user_id }) => {
    const [activeTab, setActiveTab] = useState(0); // 0=Vitals, 1=Custom, 2=Team History
    const [loading, setLoading] = useState(false);
    const [vitals, setVitals] = useState([]);
    const [meta, setMeta] = useState({ source: 'user', case_custom_vitals: [], user_custom_vitals: [], first_primary_id: null });
    const [error, setError] = useState(null);

    const [caseCustomVitals, setCaseCustomVitals] = useState([]);
    const [sourceChoice, setSourceChoice] = useState('user');
    const [newFieldId, setNewFieldId] = useState('');
    const [newCustomName, setNewCustomName] = useState('');
    const [newCustomType, setNewCustomType] = useState(1);
    const [availableFieldOptions, setAvailableFieldOptions] = useState({});

    const [fieldTypes, setFieldTypes] = useState(defaultTypeOptions);
    const [users, setUsers] = useState([]);

    const hasFirstPrimaryVital = useMemo(
        () => vitals.some(vital => vital?.name === 'First Primary' || Number(vital?.field_id) === -2),
        [vitals]
    );

    useEffect(() => {
        if (!hasFirstPrimaryVital) return undefined;

        let mounted = true;
        fetchUsers().then((result) => {
            if (mounted) {
                setUsers(Array.isArray(result) ? result : []);
            }
        });

        return () => {
            mounted = false;
        };
    }, [hasFirstPrimaryVital]);

    const userOptions = useMemo(() => users.reduce((options, user) => {
        if (user?.id == null || String(user?.access_level || '').trim().toLowerCase() === 'no access') {
            return options;
        }
        options[user.id] = user.name || user.user || `User ${user.id}`;
        return options;
    }, {}), [users]);

    const fieldTypeLabelById = useMemo(() => {
        const map = new Map();
        fieldTypes.forEach(ft => {
            if (ft?.id != null) {
                map.set(Number(ft.id), ft.label);
            }
        });
        return map;
    }, [fieldTypes]);

    const fieldMetaById = useMemo(() => {
        const map = new Map();
        vitals.forEach(v => {
            if (v?.field_id != null) {
                const typeLabel = fieldTypeLabelById.get(Number(v.type_id)) || fieldTypeLabelById.get(Number(v.field_type_id));
                map.set(Number(v.field_id), {
                    name: v.name,
                    typeLabel: typeLabel || null
                });
            }
        });
        return map;
    }, [vitals, fieldTypeLabelById]);

    // Inline vital editor that mirrors Section.js field rendering by type_id
    const Vital = ({ vital, onSave }) => {
        const isFirstPrimary = vital?.name === 'First Primary' || Number(vital?.field_id) === -2;
        const storedValue = isFirstPrimary ? vital?.original_value : vital?.value;
        const [value, setValue] = useState(storedValue ?? '');

        useEffect(() => {
            setValue(storedValue ?? '');
        }, [storedValue]);

        const typeId = Number(vital?.type_id ?? vital?.field_type_id ?? vital?.field_id ?? 1);

        const handleCommit = async (val) => {
            setValue(val);
            if (String(val ?? '') === String(storedValue ?? '')) return;
            await onSave(val);
        };

        // Render using the same components and behaviors as Section.js
        const renderInput = () => {
            if (isFirstPrimary) {
                return (
                    <SearchSelect
                        ariaLabel='First Primary'
                        value={value}
                        onChange={handleCommit}
                        options={userOptions}
                        placeholder='Select a user...'
                    />
                );
            }

            switch (typeId) {
                case 1:
                    return (
                        <Text
                            type='text'
                            placeholder={vital?.name}
                            value={value}
                            onChange={setValue}
                            onBlur={() => handleCommit(value)}
                            onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
                        />
                    );
                case 2:
                    return (
                        <Text
                            type='textarea'
                            placeholder={vital?.name}
                            value={value}
                            onChange={setValue}
                            onBlur={() => handleCommit(value)}
                        />
                    );
                case 3:
                    return (
                        <NumberInput
                            type='currency'
                            value={value}
                            onChange={setValue}
                            onBlur={() => handleCommit(value)}
                            onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
                        />
                    );
                case 4:
                    return (
                        <NumberInput
                            type='percent'
                            value={value}
                            onChange={setValue}
                            onBlur={() => handleCommit(value)}
                            onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
                        />
                    );
                case 5:
                    return (
                        <NumberInput
                            type='number'
                            value={value}
                            onChange={setValue}
                            onBlur={() => handleCommit(value)}
                            onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
                        />
                    );
                case 6:
                    return (
                        <DateInput
                            value={String(value || '')}
                            onChange={handleCommit}
                        />
                    );
                case 7:
                    return (
                        <TimeInput
                            value={value}
                            onChange={handleCommit}
                        />
                    );
                case 10:
                    return (
                        <Dropdown
                            placeholder={vital?.name}
                            options={vital?.options || []}
                            value={value}
                            onChange={handleCommit}
                        />
                    );
                case 11:
                    return (
                        <MultiSelect
                            options={vital?.options || []}
                            value={value}
                            onChange={handleCommit}
                        />
                    );
                case 12:
                    return (
                        <Boolean
                            options={vital?.options || []}
                            value={value != null && value != undefined ? Number(value) : 2}
                            onChange={(selectedValue) => handleCommit(selectedValue)}
                        />
                    );
                case 13:
                    return <Subheader title={vital?.name} />;
                case 14:
                    return <Instructions instructions={vital?.name} />;
                case 20: {
                    const deadlineValue = parseDeadlineVitalValue(value);
                    const hasDoneDate = !!deadlineValue.done;
                    const handleDeadlineCommit = (key) => (nextValue) => {
                        const nextDeadlineValue = {
                            ...deadlineValue,
                            [key]: nextValue
                        };
                        handleCommit(serializeDeadlineVitalValue(nextDeadlineValue));
                    };

                    return (
                        <div className='vital-deadline-inputs' style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <div className='deadline-date' style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                                <label className='subtext'>Due</label>
                                <DateInput
                                    value={deadlineValue.due}
                                    onChange={handleDeadlineCommit('due')}
                                />
                            </div>
                            {hasDoneDate && (
                                <div className='deadline-date' style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                                    <label className='subtext'>Done</label>
                                    <DateInput
                                        value={deadlineValue.done}
                                        onChange={handleDeadlineCommit('done')}
                                        checkbox
                                    />
                                </div>
                            )}
                        </div>
                    );
                }
                default:
                    return (
                        <Text
                            type='text'
                            placeholder={vital?.name}
                            value={value}
                            onChange={setValue}
                            onBlur={() => handleCommit(value)}
                            onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
                        />
                    );
            }
        };

        // For subheader/instructions, render bare; otherwise wrap with label/container
        if ([13, 14].includes(typeId)) {
            return (
                <div className='form-group nm vital-card'>
                    {renderInput()}
                </div>
            );
        }

        return (
            <div className='form-group nm vital-card' style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label className='subtext' style={{ fontWeight: 600 }}>{vital?.name}</label>
                <div className='vital-input-date'>
                    {renderInput()}
                    {vital?.user_id && <span className='subtext'>Updated by {vital?.user_name} (@{vital?.user_username}) at {toCst(vital?.updated_at)}</span>}
                </div>
            </div>
        );
    };

    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const currentCaseTypeId = useMemo(() => {
        const val = caseData?.lead?.case_type_id;
        return val != null ? parseInt(val) : null;
    }, [caseData?.lead?.case_type_id]);

    const currentTemplateId = useMemo(() => {
        const leadTemplate = caseData?.lead?.template_id;
        const caseTemplate = caseData?.case?.template_id;
        if (leadTemplate != null) return leadTemplate;
        if (caseTemplate != null) return caseTemplate;
        return 1;
    }, [caseData?.lead?.template_id, caseData?.case?.template_id]);

    const loadVitals = async () => {
        if (!caseId) return;
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch(`https://api.casedb.co/custom_vitals.php?case_id=${caseId}&t=${Date.now()}`);
            const data = await resp.json();
            if (data.success) {
                setVitals(Array.isArray(data.vitals) ? data.vitals : []);
                const metaData = data.meta || {};
                setMeta(metaData);
                setCaseCustomVitals(metaData.case_custom_vitals || []);
                setSourceChoice(metaData.source || 'user');
            } else {
                setError(data.message || 'Failed to load vitals');
            }
        } catch (e) {
            console.error('Error loading vitals', e);
            setError('Error loading vitals');
        } finally {
            setLoading(false);
        }
    };

    const loadHistory = async () => {
        if (!caseId) return;
        setHistoryLoading(true);
        try {
            const resp = await fetch(`https://api.casedb.co/custom_vitals.php?case_id=${caseId}&history=1&t=${Date.now()}`);
            const data = await resp.json();
            if (data.success) {
                setHistory(data.history || []);
            }
        } catch (e) {
            console.error('Error loading history', e);
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        loadVitals();
    }, [caseId]);

    // Fetch up-to-date field types from API; fallback to defaults on failure
    useEffect(() => {
        const fetchFieldTypes = async () => {
            try {
                const resp = await fetch('https://api.casedb.co/fields.php');
                const data = await resp.json();
                if (data?.success && Array.isArray(data.fields)) {
                    const mapped = data.fields
                        .filter(f => f?.id && ![13, 14, 19].includes(Number(f.id)) && Number(f.id) < 21)
                        .map(f => ({ id: Number(f.id), label: f.name }))
                        .filter(f => !Number.isNaN(f.id));
                    if (mapped.length > 0) {
                        setFieldTypes(mapped);
                        return;
                    }
                }
                console.warn('Using default field types (API returned empty or unexpected).');
                setFieldTypes(defaultTypeOptions);
            } catch (e) {
                console.error('Error fetching field types', e);
                setFieldTypes(defaultTypeOptions);
            }
        };

        fetchFieldTypes();
    }, []);

    useEffect(() => {
        if (activeTab === 2) {
            loadHistory();
        }
    }, [activeTab]);

    useEffect(() => {
        const fetchAvailableFields = async () => {
            if (!currentTemplateId) return;
            try {
                const resp = await fetch(`https://api.casedb.co/custom_fields.php?template_id=${currentTemplateId}&time=${Date.now()}`, {
                    credentials: 'include'
                });
                const data = await resp.json();
                const opts = Array.isArray(data?.custom_fields)
                    ? Object.fromEntries(
                        data.custom_fields
                            .filter(cf => cf?.id != null && cf?.name && ![13, 14, 22].includes(Number(cf.field_id)))
                            .map(cf => [cf.id, cf.name])
                    )
                    : {};
                setAvailableFieldOptions(opts);
            } catch (e) {
                console.error('Error fetching available custom fields', e);
                setAvailableFieldOptions({});
            }
        };

        fetchAvailableFields();
    }, [currentTemplateId]);

    const firstPrimaryId = useMemo(() => {
        if (meta?.first_primary_id) return meta.first_primary_id;
        const fp = vitals?.find(v => v.name === 'First Primary' || v.field_id === -2);
        return fp?.original_value ?? null;
    }, [meta?.first_primary_id, vitals]);

    const filteredVitals = useMemo(() => {
        return (vitals || []).filter(v => {
            if (!v) return false;
            if (v.name === 'First Primary' || v.field_id === -2) return true;

            // Always show entries without a saved vital row
            if (v.vital_id == null) return true;

            // Treat null/0 case_type_id as global so orphan custom_fields still render
            const vitalCt = v.case_type_id != null ? parseInt(v.case_type_id, 10) : null;
            if (vitalCt === null || Number.isNaN(vitalCt) || vitalCt === 0) return true;

            if (currentCaseTypeId == null) return false;
            return vitalCt === currentCaseTypeId;
        });
    }, [vitals, currentCaseTypeId]);

    const updateVital = async (field, newValue, table, customVitalName = null) => {
        if (!caseId) return;
        try {
            const payload = {
                case_id: caseId,
                lead_id: leadId,
                field,
                value: newValue,
                table
            };

            if (customVitalName) {
                payload.custom_vital_name = customVitalName;
                payload.is_custom_vital = true;
                if (firstPrimaryId) payload.user_id = firstPrimaryId;
            }

            const response = await fetch('https://api.casedb.co/update-vital.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.success) {
                await loadVitals();
                fetchVitals?.();
                if (table === 'cases' || table === 'leads') {
                    fetchCase?.(true);
                }
            } else {
                setError(result.message || 'Failed to update vital');
            }
        } catch (e) {
            console.error('Error updating vital', e);
            setError('Error updating vital');
        }
    };

    const addFieldIdVital = () => {
        if (!newFieldId) return;
        const idNum = parseInt(newFieldId);
        if (isNaN(idNum)) return;
        if (!caseCustomVitals.includes(idNum)) {
            setCaseCustomVitals(prev => [...prev, idNum]);
        }
        setNewFieldId('');
    };

    const addCustomVital = () => {
        if (!newCustomName.trim()) return;
        const entry = {};
        entry[newCustomName.trim()] = { value: '', field_type_id: newCustomType };
        setCaseCustomVitals(prev => [...prev, entry]);
        setNewCustomName('');
        setNewCustomType(1);
    };

    const removeCaseVital = (index) => {
        setCaseCustomVitals(prev => prev.filter((_, i) => i !== index));
    };

    const renderVitalList = (list, allowRemove = false) => {
        if (!list || list.length === 0) {
            return <span className='subtext'>No custom vitals defined.</span>;
        }

        return list.map((item, idx) => {
            // Existing field reference by ID
            if (typeof item === 'number' || (!isNaN(item) && typeof item !== 'object')) {
                const metaForField = fieldMetaById.get(Number(item));
                const label = metaForField?.typeLabel;
                const name = metaForField?.name;
                return (
                    <div key={`field-${idx}`} className='vital-list-item'>
                        <span>
                            <span className='subtext'>Field:</span> {name || `ID ${item}`} {label ? `(${label})` : ''}
                        </span>
                        {allowRemove && <button className='action tertiary' onClick={() => removeCaseVital(idx)}>Remove</button>}
                    </div>
                );
            }

            // Custom-defined vital object
            if (typeof item === 'object' && item !== null) {
                const name = Object.keys(item)[0];
                const def = item[name];
                const fieldTypeId = def?.field_type_id ?? def?.field_type ?? 1;
                const label = fieldTypeLabelById.get(fieldTypeId);

                return (
                    <div key={`custom-${idx}`} className='vital-list-item'>
                        <span>
                            <span className='subtext'>Custom:</span> {name} {label ? `(${label})` : `(type id: ${fieldTypeId})`}
                        </span>
                        {allowRemove && <button className='action tertiary' onClick={() => removeCaseVital(idx)}>Remove</button>}
                    </div>
                );
            }

            return null;
        });
    };

    const saveCustomConfig = async () => {
        if (!caseId) return;
        setLoading(true);
        setError(null);
        try {
            const payload = {
                case_id: caseId,
                custom_vitals_source: sourceChoice,
            };
            if (sourceChoice === 'case') {
                payload.custom_vitals = caseCustomVitals;
            }

            const resp = await fetch('https://api.casedb.co/custom_vitals.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await resp.json();
            if (data.success) {
                await loadVitals();
                fetchVitals?.();
            } else {
                setError(data.message || 'Failed to save custom vitals');
            }
        } catch (e) {
            console.error('Error saving custom vitals', e);
            setError('Error saving custom vitals');
        } finally {
            setLoading(false);
        }
    };

    const orderedVitals = useMemo(() => {
        const list = Array.isArray(filteredVitals) ? [...filteredVitals] : [];
        const fpIdx = list.findIndex(v => v?.name === 'First Primary' || v?.field_id === -2);
        if (fpIdx > 0 && list.length > 1) {
            const [fp] = list.splice(fpIdx, 1);
            list.splice(1, 0, fp); // Ensure First Primary is second
        }
        return list;
    }, [filteredVitals]);

    const renderVitalsTab = () => (
        <div className='case-vitals-container'>
            {orderedVitals?.map((vital, index) => (
                <Vital
                    key={vital?.id || `vital-${index}`}
                    vital={vital}
                    onSave={(newVal) => {
                        const isFirstPrimary = vital?.name === 'First Primary' || Number(vital?.field_id) === -2;
                        return updateVital(
                            isFirstPrimary ? 'first_primary_id' : vital?.field_id,
                            newVal,
                            isFirstPrimary ? 'cases' : 'custom_fields',
                            !isFirstPrimary && !vital?.field_id ? vital?.name : null
                        );
                    }}
                />
            ))}
        </div>
    );

    const renderCustomTab = () => (
        <div className='custom-vitals-editor'>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ color: 'var(--fill)' }}>Vitals preset:</label>
                <NeutralToggle 
                    options={[
                        { label: 'First Primary', value: 'user' },
                        { label: 'Custom', value: 'case' }
                    ]}
                    value={sourceChoice}
                    onChange={setSourceChoice}
                />
            </div>

            {sourceChoice === 'case' ? (
                <>
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                        <div className='form-group nm'>
                            <label className='subtext'>Add Field</label>
                            <div style={{ display: 'flex', gap: '5px', width: '100%' }}>
                                <div style={{ maxWidth: '95%', width: '100%' }}>
                                    <SearchSelect
                                        value={newFieldId || ''}
                                        onChange={(val) => setNewFieldId(val ? String(val) : '')}
                                        options={availableFieldOptions}
                                        placeholder='Search by field name'
                                    />
                                </div>
                                <button className='action alt' onClick={addFieldIdVital} disabled={!newFieldId}>Add</button>
                            </div>
                        </div>
                        <div className='form-group nm' style={{ width: '100%' }}>
                            <label className='subtext'>Add Custom Vital</label>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', width: '100%' }}>
                                <input
                                    type='text'
                                    value={newCustomName}
                                    onChange={(e) => setNewCustomName(e.target.value)}
                                    placeholder='Name'
                                    style={{ maxWidth: '75%' }}
                                />
                                <div className="filter-group">
                                    <div className="select-wrapper">
                                        <select
                                            value={newCustomType}
                                            onChange={(e) => setNewCustomType(Number(e.target.value))}
                                            className='filter-select'
                                        >
                                            {fieldTypes.map(opt => (
                                                <option key={opt.id} value={opt.id}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                </div>
                                <button className='action alt' onClick={addCustomVital}>Add</button>
                            </div>
                        </div>
                    </div>

                    <div className='vital-list'>
                        {renderVitalList(caseCustomVitals, true)}
                    </div>
                </>
            ) : (
                <div style={{ marginBottom: '12px' }}>
                    <span className='subtext'>Using First Primary presets (read-only). Edit that user’s custom vitals to change this list.</span>
                    <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <strong>Current presets</strong>
                        <div className='list' style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {renderVitalList(meta?.user_custom_vitals || [], false)}
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
                <button className='action' onClick={saveCustomConfig} disabled={loading}>
                    {loading ? 'Saving...' : 'Save'}
                </button>
                <button className='action tertiary' onClick={loadVitals} disabled={loading}>
                    Refresh
                </button>
            </div>
        </div>
    );

    const renderHistoryTab = () => {
        const displayValue = (row) => {
            if (!row) return '';
            const val = row.value;
            if (val === null || val === undefined) return '';

            // If options exist and value is an index, show the option label
            if (row.options) {
                try {
                    const opts = JSON.parse(row.options);
                    const idx = Number(val);
                    if (Array.isArray(opts) && !Number.isNaN(idx) && idx >= 0 && idx < opts.length) {
                        return opts[idx];
                    }
                } catch (e) {
                    // fall through
                }
            }

            return val;
        };

        const displayName = (row) => {
            return row?.custom_vital_name
                ?? row?.vital_name
                ?? row?.field_name
                ?? row?.name
                ?? '';
        };

        return (
            <div className='employee-info' style={{ marginBottom: '20px', overflowY: 'auto' }}>
                {historyLoading && <div>Loading history...</div>}
                {!historyLoading && history?.length === 0 && <div className='subtext'>No history recorded.</div>}
                {!historyLoading && history?.length > 0 && (
                    <>
                        <div className='employee-info-header' style={{ padding: '8px 16px' }}>
                            <div className='table-title' style={{ height: '28px' }}>Vital Updates</div>
                        </div>
                        <table className='employee-data-table'>
                            <thead>
                                <tr className='subtext'>
                                    <th>Vital</th>
                                    <th>Value</th>
                                    <th>User</th>
                                    <th>Updated</th>
                                </tr>
                            </thead>
                            <tbody className='small-text'>
                                {history.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{displayName(row)}</td>
                                        <td>{displayValue(row)}</td>
                                        <td>{row.user_name ?? row.user_id ?? 'Unknown'}</td>
                                        <td>{toCst(row.updated_at ?? row.created_at ?? '')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className='case-section'>
            <div id='template-header' className='alt-header'>
                {window.innerWidth < 729 ? (
                    <div className="filter-group" style={{ width: '100%' }}>
                        <div className="select-wrapper">
                            <select className='filter-select default-select' value={activeTab} onChange={(e) => setActiveTab(Number(e.target.value))}>
                                <option value={0}>Vitals</option>
                                <option value={1}>Custom Vitals</option>
                                <option value={2}>Team History</option>
                            </select>
                            <ChevronDown size={16} className="select-chevron" />
                        </div>
                    </div>
                ) : (
                    <>
                        <h4 onClick={() => setActiveTab(0)} className={`${activeTab !== 0 ? 'in' : ''}active`}>Vitals</h4>
                        <h4 onClick={() => setActiveTab(1)} className={`${activeTab !== 1 ? 'in' : ''}active`}>Custom Vitals</h4>
                        <h4 onClick={() => setActiveTab(2)} className={`${activeTab !== 2 ? 'in' : ''}active`}>Team History</h4>
                    </>
                )}
            </div>

            {error && <div className='error'>{error}</div>}
            {loading && activeTab !== 2 && <div>Loading...</div>}

            {activeTab === 0 && renderVitalsTab()}
            {activeTab === 1 && renderCustomTab()}
            {activeTab === 2 && renderHistoryTab()}
        </div>
    );
};

export default Vitals;
